/**
 * Content Frame loads views such as mobile phone, CTV screen, fake pages, etc.
 * It should allow to center both vertically and horizontally the inner components, 
 * or allow to scroll the view.
 */


import { useState, useEffect, useRef, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';

import CTV from '../Ctv';
import EditorView from '../EditorView';
import CoolPage from '../CoolPage/CoolPage';
import MobileView from '../MobileView';
import BannerView from 'components/molecules/BannerView';
import { PLAYBACK_TYPE } from 'services/constants';
import { setShowBannerOptions } from 'context/actions';
import { CreativeType, DisplayBannerT } from 'types/creative';
import { useAppState, useAppDispatch } from 'context';
import { useViewport } from 'hooks';
import { getPlaybackName } from 'services/utils';
import './ContentFrame.scss';


const ContentFrame = () => {
  const {
    mobileView,
    webView,
    screenSizeClass,
    creative = { media_asset: { metadata: { aspect_ratio: 0 } } } as CreativeType,
    displayBanner = {} as DisplayBannerT,
    error,
    superFrameLoaded,
    activeCreativeView
  } = useAppState();

  const alignPadding = 50;

  const dispatch = useAppDispatch();
  const scrollWrapper = useRef<HTMLDivElement>(null);
  const viewPort = useViewport();

  const [alignTop, setAlignTop] = useState(false);
  const [alignLeft, setAlignLeft] = useState(false);

  const { active: activeBannerView } = displayBanner;
  const { share:shareView } = activeCreativeView;
  const isCtv = creative.playback_id === PLAYBACK_TYPE.ctv;
  const isTikTok = creative.playback_id === PLAYBACK_TYPE.tiktok;
  const playbackClass = getPlaybackName(creative.playback_id);
  const cssPlatform = isMobileOnly ? 'mobile' : 'desktop';
  const cssLayout = shareView ? 'share' : 'non-share';
  // hide mobile options here
  const hideBannerOptions = useMemo(() => {
    if (!creative.playback_id) return true;
   
    // no possible banners in this playbacks
    const isPlayback = [PLAYBACK_TYPE.ctv, PLAYBACK_TYPE.tiktok, PLAYBACK_TYPE.lite, PLAYBACK_TYPE.max].includes(creative.playback_id);
    if( isPlayback ) return true;

    // if video media type, not on playbacks
    if( creative.media_type ===  1) {
      const isMediaVideo = [ PLAYBACK_TYPE.maxo3].includes(creative.playback_id);
      return isMediaVideo;
    } else {
      // image media type on all possible playbacks 
      return false;
    }
  }, [creative.playback_id]);

  const opts = useMemo(() => {

    // prevent loading default when there is no creative data
    if (!Object.keys(creative).length) return {}; 

    // always show CTV view when is CTV
    if (isCtv) return { component: <CTV />, name: 'ctv-view', allowBanner: false, }
    if (isTikTok && !isMobileOnly) return { component: <MobileView />, name: 'tiktok-view', allowBanner: false, }

    if(isMobileOnly) {
      if (activeBannerView) return { component: <BannerView />, name: 'banner-view', allowBanner: true }
    } else { 
      if (activeBannerView && !mobileView) return { component: <BannerView />, name: 'banner-view', allowBanner: true }
      if (webView) return { component: <CoolPage />, name: 'webpage-view', allowBanner: false }
      if (mobileView) return { component: <MobileView />, name: 'mobile-view', allowBanner: true }
    }

    return { component: <EditorView />, name: 'default-view', allowBanner: true }
  }, [creative.playback_id, activeBannerView, mobileView])


  // Handle
  useEffect(() => {

    if (isMobileOnly) { // not on mobile phones
      setAlignTop(false);
      setAlignLeft(false);
      return;
    };

    if (scrollWrapper.current) {
      if (scrollWrapper.current.scrollHeight > scrollWrapper.current.clientHeight + alignPadding) {
        setAlignTop(true);
      } else {
        setAlignTop(false);
      }
      if (scrollWrapper.current.scrollWidth > scrollWrapper.current.clientWidth + alignPadding) {
        setAlignLeft(true)
      } else {
        setAlignLeft(false)
      }
    }
  }, [viewPort]);

  useEffect(() => {
    if (!shareView && opts.allowBanner && !hideBannerOptions ) {
      dispatch(setShowBannerOptions(true))
    }
  }, [creative, activeBannerView, opts, shareView])


  if (!Object.keys(creative).length || !superFrameLoaded) {
    let errorMessage = 'Gathering information...';
    
    if (error) {
      if (typeof error === 'object' && error !== null) {
        // Safely extract the message from the error object
        errorMessage = error.message || 'An unexpected error occurred';
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else {
        errorMessage = 'An unexpected error occurred';
      }
    }
    
    return (
      <div className='loading'>
        {error ? (
          <div className="error-message">
            Error: {errorMessage}
          </div>
        ) : (
          'Gathering information...'
        )}
      </div>
    );
  }

  return (
    <div className={`content-frame ${screenSizeClass} ${playbackClass} ${cssPlatform} ${opts.name} ${cssLayout} ${alignTop ? 'align-top' : ''} ${alignLeft ? 'align-left' : ''}`} ref={scrollWrapper}>
      {opts.component}
    </div>
  );
};

export default ContentFrame;
